/* eslint-disable jsx-a11y/tabindex-no-positive */
import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import Layout from '../Layout';
import LoadingOverlay from '../Helper/LoadingOverlay';
import TextField from '../Form/TextField';
import PrivacyText from '../Helper/PrivacyText';
import PasswordField from '../Form/PasswordField';
import Link from '../Helper/Link';
import CgCaptcha from '../Recaptcha/CgCaptcha';
import { InputError } from '../Form/errors';
import { CaptchaTypes } from '../../constants';
import { TOTPForm } from '../Login/TOTPForm';
import { FREE_PRODUCTS } from './constants';

const Login = withTranslation()(
  ({
    onContinue,
    onPasswordChange,
    onCGCaptchaChange,
    onReCaptchaValidate,
    generateCgCaptcha,
    socialLogin,
    data,
    cgcaptcha,
    captchaType,
    fieldErrors,
    error,
    requestTOTP,
    onSubmitTOTP,
    onResetTOTP,
    loading,
    app,
    routing,
  }) => {
    return (
      <Layout
        className="trial login"
        reCaptchaValidateHandler={captchaType === CaptchaTypes.RECAPTCHA ? onReCaptchaValidate : null}
        cover={{ src: '/images/cover-main.png' }}
      >
        <LoginForm
          data={data}
          fieldErrors={fieldErrors}
          error={error}
          app={app}
          loading={loading}
          socialLogin={socialLogin}
          generateCgCaptcha={generateCgCaptcha}
          cgcaptcha={cgcaptcha}
          captchaType={captchaType}
          requestTOTP={requestTOTP}
          onSubmitTOTP={onSubmitTOTP}
          onResetTOTP={onResetTOTP}
          onContinue={onContinue}
          onCGCaptchaChange={onCGCaptchaChange}
          onPasswordChange={onPasswordChange}
          routing={routing}
        />
      </Layout>
    );
  },
);

const LoginForm = withTranslation()(
  ({
    data,
    fieldErrors,
    error,
    reCaptcha,
    loading,
    socialLogin,
    app,
    onContinue,
    onPasswordChange,
    onCGCaptchaChange,
    generateCgCaptcha,
    cgcaptcha,
    captchaType,
    requestTOTP,
    onSubmitTOTP,
    onResetTOTP,
    routing,
    t,
  }) => {
    const buttonsDisabled = app.loading || app.redirecting;
    const isFreeProduct = FREE_PRODUCTS.includes(routing?.query?.productCode);

    if (requestTOTP) {
      return <TOTPForm onSubmit={onSubmitTOTP} onBack={onResetTOTP} loading={app.loading} error={error} />;
    }

    return (
      <div className="wrapper-shrink">
        <h1 className="title">{t('Enter your email.')}</h1>
        {isFreeProduct ? (
          <p className="subtitle">{t('You will use it to access your product.')}</p>
        ) : (
          <p className="subtitle">{t('You will use it to access your trial.')}</p>
        )}
        <LoadingOverlay active={loading || socialLogin.loading || app.redirecting} />
        <form
          id="form-login"
          method="post"
          onSubmit={(e) => {
            e.preventDefault();
            onContinue(reCaptcha);
          }}
        >
          <TextField
            id="email"
            inputName="email"
            value={data.email}
            label={t('Work or educational email')}
            icon={{ src: '../images/padlock.svg', className: 'custom-icon' }}
            disabled
            error={fieldErrors.email}
          >
            <Link id="change-email" tabIndex="4" action="TRIAL_EMAIL_CHECK">
              {t('Change email')}
            </Link>
          </TextField>
          <PasswordField
            inputName="password"
            value={data.password || ''}
            label={t('Password')}
            error={fieldErrors.password}
            showHideButton
            tabIndex="2"
            disabled={buttonsDisabled}
            onChange={(e) => onPasswordChange(e.target.value)}
          >
            <Link id="link-recover-account" tabIndex="4" action="TRIAL_PASSWORD_RECOVER">
              {t('Forgot your password?')}
            </Link>
          </PasswordField>

          {fieldErrors.captcha && <InputError err={fieldErrors.captcha} />}
          {captchaType === CaptchaTypes.CG_CAPTCHA && (
            <CgCaptcha
              value={cgcaptcha.captchaValue}
              imageUrl={cgcaptcha.imageUrl}
              domain={window.CG_CAPTCHA_DOMAIN}
              loading={cgcaptcha.loading}
              onInputChange={(e) => {
                e.preventDefault();
                onCGCaptchaChange(e.target.value);
              }}
              onCaptchaRefresh={(e) => {
                e.preventDefault();
                generateCgCaptcha();
              }}
            />
          )}

          <PrivacyText className="privacy-text" i18nKey="privacy-text-trial" />
          <div className="login-button-wrapper">
            <button
              id="btn-login"
              form="form-login"
              type="submit"
              name="commit"
              value={t('Continue')}
              className="button btn-active"
              disabled={buttonsDisabled}
              tabIndex="3"
            >
              {t('Continue')}
            </button>
          </div>
        </form>
      </div>
    );
  },
);
export default connect(
  (state) => ({
    data: state.trial.fields,
    captchaType: state.trial.loginCaptcha.captchaType,
    cgcaptcha: state.trial.loginCaptcha.cgcaptcha,
    fieldErrors: state.trial.fieldErrors,
    loading: state.trial.loading,
    socialLogin: state.socialLogin,
    requestTOTP: state.trial.requestTOTP,
    error: state.trial.error,
    app: state.app,
    routing: state.routing,
  }),

  (dispatch) => ({
    onPasswordChange: (password) => dispatch({ type: 'TRIAL_INPUT_CHANGE', data: { password } }),
    onCGCaptchaChange: (cgCaptchaValue) => dispatch({ type: 'TRIAL_INPUT_CHANGE', data: { cgCaptchaValue } }),
    generateCgCaptcha: () => dispatch({ type: 'GENERATE_CG_LOGIN_CAPTCHA', isTrial: true }),
    onContinue: (reCaptcha) => dispatch({ type: 'SUBMIT_LOGIN', reCaptcha, isTrial: true }),
    onReCaptchaValidate: (value) =>
      dispatch({ type: 'SUBMIT_LOGIN#VALIDATE_RECAPTCHA', data: { value, isTrial: true } }),
    resendActivationCode: (email) => dispatch({ type: 'RESEND_ACTIVATION_CODE', data: { email } }),
    onSubmitTOTP: (totpCode) => dispatch({ type: 'SUBMIT_TOTP', totpCode, isTrial: true }),
    onResetTOTP: () => dispatch({ type: 'RESET_TRIAL_LOGIN_TOTP' }),
  }),
)(Login);
